import styled from '@emotion/styled';
import { useNavBarState } from 'app/navBarController';
import Loader from 'components/Kizen/Loader';
import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import { useSetTitleOnLoad } from 'hooks/useSetTitleOnLoad';
import { ErrorMessage } from 'components/ErrorMessage';
import {
  DashboardErrorCodeText,
  DashboardWarningText,
} from 'pages/Dashboard/styles';
import { PluginContext } from 'ts-components/Plugins/context';
import {
  PluginView,
  usePluginViewPage,
} from 'ts-components/Plugins/PluginView';

const Container = styled.div`
  height: calc(100vh - ${(props) => props.navbarHeight}px);
  position: relative;
`;

const AbsoluteLoader = styled(Loader)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
  background: transparent;
`;

const showLoader = (type, pending, frameLoaded) => {
  if (type === 'iframe') {
    return !frameLoaded;
  }
  if (type === 'html') {
    return false;
  }
  return pending;
};

const FullPagePlugin = ({ location }) => {
  const params = useParams();
  const { t } = useTranslation();

  const { appAPIName, pageAPIName } = params;

  const { pluginNames, isLoading } = useContext(PluginContext);

  const pluginNameIsValid = useMemo(() => {
    return pluginNames.includes(appAPIName);
  }, [appAPIName, pluginNames]);

  const currentPage = usePluginViewPage({
    pluginApiName: appAPIName,
    viewApiName: pageAPIName,
  });

  const { height: navbarHeight } = useNavBarState();

  const pageTitle = currentPage
    ? `${currentPage?.plugin_name} - ${currentPage?.name}`
    : t('Unknown App');

  useSetTitleOnLoad(pageTitle);

  if (isLoading) {
    return <Loader loading />;
  }

  if (!currentPage && pluginNameIsValid) {
    return (
      <ErrorMessage
        title={t('Page Not Found')}
        text={
          <DashboardWarningText as="p">
            {t(
              'This page does not exist. Please contact your administrator if you believe this is in error.'
            )}
          </DashboardWarningText>
        }
        code={
          <DashboardErrorCodeText as="p">
            {t('Error code')}: 404
          </DashboardErrorCodeText>
        }
        dashboard
      />
    );
  }

  if (!currentPage) {
    return (
      <ErrorMessage
        title={t('Plugin Not Found')}
        text={
          <DashboardWarningText as="p">
            {t(
              'This plugin does not exist. Please contact your administrator if you believe this is in error.'
            )}
          </DashboardWarningText>
        }
        code={
          <DashboardErrorCodeText as="p">
            {t('Error code')}: 404
          </DashboardErrorCodeText>
        }
        dashboard
      />
    );
  }

  return (
    <PluginView
      pluginApiName={appAPIName}
      viewApiName={pageAPIName}
      page={currentPage}
      search={location.search}
      isLoading={isLoading}
      sizing={currentPage.type === 'html' ? 'page' : 'fill'}
      iframeTitle={currentPage.plugin_name}
    >
      {(body, { pending, frameLoaded }) => (
        <Container navbarHeight={navbarHeight}>
          {showLoader(currentPage.type, pending, frameLoaded) ? (
            <AbsoluteLoader loading />
          ) : null}

          {body}
        </Container>
      )}
    </PluginView>
  );
};

export default function PluginFullPage() {
  const location = useLocation();

  return <FullPagePlugin key={location.key} location={location} />;
}
